import clsx from "clsx";
import React from "react";

import { useDrawolaT } from "../i18n";
import { formatMB } from "../quota";
import { LIMITS } from "../types";

import type { StorageSnapshot } from "../types";

type Translate = (
  key: string,
  replacement?: { [key: string]: string | number },
) => string;

type ViewProps = {
  snapshot: StorageSnapshot | null;
  /**
   * Translator, INJECTED rather than hooked.
   *
   * `useDrawolaT()` goes through upstream's `useI18n()`, whose jotai-scope
   * isolation only exists inside the `<Excalidraw>` tree. The Studio renders
   * outside it (see studio/i18n.ts), so keeping this component hook-free is
   * what lets the same meter serve both places.
   */
  t: Translate;
  /**
   * `"compact"` keeps only the bar and a one-line summary — sized for the
   * Studio top bar. `"full"` (default) is the Files-sidebar layout.
   */
  variant?: "full" | "compact";
};

/**
 * Single three-zone storage bar. Total length represents policy max (10 GB).
 * Zones:
 *   - used         (solid primary; turns amber/red near limits)
 *   - available    (light primary) = min(policyTotal, quota*0.9) - used
 *   - unreachable  (hatched grey)  = policyTotal - min(policyTotal, quota*0.9)
 */
export const StorageMeterView: React.FC<ViewProps> = ({
  snapshot,
  t,
  variant = "full",
}) => {
  const compact = variant === "compact";
  const rootClass = clsx("drawOla-storage-meter", {
    "drawOla-storage-meter--compact": compact,
  });

  if (!snapshot) {
    return (
      <div className={clsx(rootClass, "drawOla-storage-meter--loading")} />
    );
  }
  const policy = LIMITS.POLICY_TOTAL_BYTES;
  const used = Math.max(0, Math.min(snapshot.drawolaUsage, policy));
  const effective = Math.max(used, Math.min(policy, snapshot.effectiveLimit));
  const unreachable = Math.max(0, policy - effective);
  const available = Math.max(0, effective - used);

  const usedPct = (used / policy) * 100;
  const availPct = (available / policy) * 100;
  const unreachPct = (unreachable / policy) * 100;

  const usageRatio = effective > 0 ? used / effective : 0;
  const usedColorClass =
    usageRatio >= LIMITS.BLOCK_NEW_AT
      ? "drawOla-storage-meter__used--danger"
      : usageRatio >= LIMITS.WARN_AT
      ? "drawOla-storage-meter__used--warn"
      : "drawOla-storage-meter__used--normal";

  const usedStr = formatMB(used);
  const effectiveStr = formatMB(effective);
  const availableStr = formatMB(available);
  const policyStr = formatMB(policy);

  const counterLabel = t("meter.counterLabel", {
    count: snapshot.docCount,
    max: LIMITS.MAX_DOCUMENTS,
  });

  return (
    <div className={rootClass}>
      <div
        className="drawOla-storage-meter__bar"
        role="progressbar"
        aria-valuenow={Math.round(usageRatio * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        title={t("meter.barTitle", {
          used: usedStr,
          available: effectiveStr,
          policy: policyStr,
        })}
      >
        <span
          className={`drawOla-storage-meter__used ${usedColorClass}`}
          style={{ width: `${usedPct}%` }}
          data-tooltip={t("meter.usedTooltip", { used: usedStr })}
        />
        <span
          className="drawOla-storage-meter__available"
          style={{ width: `${availPct}%` }}
          data-tooltip={t("meter.availableTooltip", {
            available: availableStr,
          })}
        />
        <span
          className="drawOla-storage-meter__unreachable"
          style={{ width: `${unreachPct}%` }}
          data-tooltip={t("meter.unreachableTooltip", {
            used: formatMB(unreachable),
          })}
        />
      </div>
      {compact ? (
        // One line, existing keys only: "Used 1.2 MB · Files 3 / 100". The
        // full breakdown stays reachable through the bar's `title`.
        <div className="drawOla-storage-meter__footer">
          <span>{t("meter.usedLine", { used: usedStr })}</span>
          <span aria-hidden>{t("meter.legendSeparator")}</span>
          <span>{counterLabel}</span>
        </div>
      ) : (
        <>
          <div className="drawOla-storage-meter__legend">
            <span>{t("meter.usedLine", { used: usedStr })}</span>
            <span aria-hidden>{t("meter.legendSeparator")}</span>
            <span>{t("meter.availableLine", { available: effectiveStr })}</span>
            <span aria-hidden>{t("meter.legendSeparator")}</span>
            <span>{t("meter.policyLine", { policy: policyStr })}</span>
          </div>
          <div className="drawOla-storage-meter__footer">
            <span>{counterLabel}</span>
            <span
              className="drawOla-storage-meter__persist"
              title={
                snapshot.persisted
                  ? t("meter.persistOnTitle")
                  : t("meter.persistOffTitle")
              }
            >
              {snapshot.persisted
                ? t("meter.persistOn")
                : t("meter.persistOff")}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Editor-tree binding of `StorageMeterView`. Used by the Files sidebar; keep
 * this the only place the meter reaches for `useDrawolaT()`.
 */
export const StorageMeter: React.FC<{ snapshot: StorageSnapshot | null }> = ({
  snapshot,
}) => {
  const t = useDrawolaT();
  return <StorageMeterView snapshot={snapshot} t={t} />;
};
